/* eslint-disable no-nested-ternary */
import {
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	Icon,
	IconButton,
	Tooltip
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import { getLabel, getDictionaryPath } from "helpers/dictionary";
import { t } from "i18next";
import lod_ from "lodash";
import DictionaryMenu from "pages/settings/filters/DictionaryMenu";
import { useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";
import { useMaterialUIController } from "context";

/**
 * Reorder columns dialog component
 * Allows users to reorder columns in a table by dragging and dropping.
 * It also allows users to add new columns from a dictionary.
 * @param {Object} props - The component props.
 * @param {Object} props.reorderDialog - The state of the reorder dialog.
 * @param {Function} props.setReorderDialog - Function to update the state of the reorder dialog.
 * @param {Array} props.header - The table header columns.
 * @param {Array} props.attributeToDisplay - The attributes to display in the table.
 * @param {string} props.collection - The collection name for the dictionary.
 * @param {Object} props.dictionary - The dictionary object containing labels and items.
 * @returns {JSX.Element} The rendered dialog component.
 */
const ReorderColumnsDialog = ({
	reorderDialog,
	setReorderDialog,
	header = [],
	attributeToDisplay = [],
	collection,
	dictionary = {},
	onSave = () => {}
}) => {
	const profile = useSelector(selectCurrentProfile);
	const language = profile?.language || "fr";
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;

	const [achorEl, setAnchorEl] = useState(null);

	const filteredHeader = header.filter(item => attributeToDisplay.includes(item.accessor));

	const [temporaryValues, setTemporaryValues] = useState(
		filteredHeader.map(item => {
			const code = item.accessor;
			const dictionaryItem = lod_.get(dictionary[collection]?.items ?? {}, getDictionaryPath(code));
			const label = getLabel(dictionaryItem?.label ?? {}, language);
			return { code, label: label ?? code };
		})
	);

	const moveItem = (fromIndex, toIndex) => {
		const newItems = [...temporaryValues];
		const [movedItem] = newItems.splice(fromIndex, 1);
		newItems.splice(toIndex, 0, movedItem);
		setTemporaryValues(newItems);
	};

	const handleDragEnd = result => {
		if (!result.destination) {
			return;
		}

		const sourceIndex = result.source.index;
		const destinationIndex = result.destination.index;

		if (sourceIndex !== destinationIndex) {
			moveItem(sourceIndex, destinationIndex);
		}
	};

	const removeItem = index => {
		const newItems = temporaryValues.filter((_, i) => i !== index);
		setTemporaryValues(newItems);
	};

	const addItem = path => {
		const item = lod_.get(dictionary, getDictionaryPath(path));
		const code = item?.code;
		const label = getLabel(item?.label ?? {}, language) || code;

		// Split path "." and remove first one
		const queryPath = path.split(".").slice(1).join(".");

		// Check if item already exists
		const exists = temporaryValues.find(item => item.code === queryPath);
		if (!exists) {
			setTemporaryValues([...temporaryValues, { code: queryPath, label }]);
		}
		setAnchorEl(null);
	};

	const handleClose = () => setReorderDialog({ open: false });

	return (
		<Dialog
			open={reorderDialog.open}
			onClose={handleClose}
			maxWidth={false}
			slotProps={{
				paper: {
					sx: ({ palette, boxShadows }) => ({
						borderRadius: 3,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
						width: "95vw",
						maxWidth: "95vw",
						m: 0,
						backgroundColor: darkMode ? palette.background.card : palette.white.main,
						boxShadow: boxShadows.xxl
					})
				}
			}}
		>
			{/* ─────────────── Header ─────────────── */}
			<MDBox
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				px={3}
				py={2.25}
				sx={({ palette, functions: { linearGradient, rgba } }) => ({
					background: linearGradient(
						rgba(palette.info.main, darkMode ? 0.25 : 0.12),
						rgba(palette.info.main, darkMode ? 0.08 : 0.03)
					)
				})}
			>
				<MDBox display="flex" alignItems="center" gap={1.75}>
					<MDBox
						display="flex"
						alignItems="center"
						justifyContent="center"
						sx={({ palette, functions: { linearGradient } }) => ({
							width: 46,
							height: 46,
							borderRadius: "12px",
							color: palette.white.main,
							background: linearGradient(palette.info.main, palette.info.focus),
							boxShadow: `0 6px 16px ${darkMode ? "rgba(0,0,0,0.4)" : "rgba(26,115,232,0.35)"}`
						})}
					>
						<Icon fontSize="medium">view_column</Icon>
					</MDBox>
					<MDBox display="flex" flexDirection="column">
						<MDBox display="flex" alignItems="center" gap={1}>
							<MDTypography variant="h5" fontWeight="bold">
								{t("DATATABLE.columnsToDisplayTitle")}
							</MDTypography>
						</MDBox>
						<MDTypography variant="button" color="text" fontWeight="regular">
							{t("DATATABLE.reorderColumnsDescription")}
						</MDTypography>
					</MDBox>
				</MDBox>

				<Tooltip title={t("SETTINGS.cancel")} placement="top" arrow>
					<MDButton
						variant="text"
						color={darkMode ? "white" : "dark"}
						iconOnly
						circular
						size="medium"
						onClick={handleClose}
					>
						<Icon>close</Icon>
					</MDButton>
				</Tooltip>
			</MDBox>

			<Divider sx={{ m: 0 }} />

			<DialogContent
				sx={{ px: 3, py: 2.5, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
			>
				<MDBox display="flex" flexDirection="column" flex="1" minHeight={0}>
					{/* Section header */}
					<MDBox
						display="flex"
						flexDirection="row"
						justifyContent="space-between"
						alignItems="center"
						mb={1.5}
					>
						<MDTypography variant="h6" fontWeight="medium">
							{t("DATATABLE.columnsToDisplayTitle")}
						</MDTypography>
						<MDButton
							variant="gradient"
							color="info"
							size="small"
							onClick={e => setAnchorEl(e.currentTarget)}
						>
							<Icon>add</Icon>&nbsp;{t("SETTINGS.add")}
						</MDButton>
						<DictionaryMenu
							placement="right"
							dictionary={dictionary}
							clickableChildsOfLevels={false}
							anchorEl={achorEl}
							handleInsertText={addItem}
							handleClose={() => setAnchorEl(null)}
						/>
					</MDBox>

					{/* Horizontal reorderable columns */}
					<DragDropContext onDragEnd={handleDragEnd}>
						<Droppable droppableId="columns" direction="horizontal">
							{(provided, snapshot) => (
								<MDBox
									{...provided.droppableProps}
									ref={provided.innerRef}
									display="flex"
									flexDirection="row"
									flexWrap="nowrap"
									alignItems="center"
									gap={1.25}
									p={1.5}
									flex="1"
									minHeight="120px"
									sx={({ palette }) => ({
										overflowX: "auto",
										overflowY: "hidden",
										borderRadius: "12px",
										border: `1px dashed ${
											snapshot.isDraggingOver
												? palette.info.main
												: darkMode
													? palette.grey[700]
													: palette.grey[300]
										}`,
										backgroundColor: snapshot.isDraggingOver
											? darkMode
												? "rgba(26,115,232,0.08)"
												: "rgba(26,115,232,0.04)"
											: darkMode
												? "rgba(255,255,255,0.02)"
												: palette.grey[100],
										transition: "background-color 0.2s ease, border-color 0.2s ease"
									})}
								>
									{temporaryValues.map((item, index) => (
										<Draggable key={item.code} draggableId={item.code} index={index}>
											{(dragProvided, dragSnapshot) => (
												<MDBox
													ref={dragProvided.innerRef}
													{...dragProvided.draggableProps}
													display="flex"
													alignItems="stretch"
													sx={({ palette, boxShadows }) => ({
														flexShrink: 0,
														borderRadius: "10px",
														overflow: "hidden",
														backgroundColor: darkMode
															? palette.background.card
															: palette.white.main,
														border: `1px solid ${
															dragSnapshot.isDragging
																? palette.info.main
																: darkMode
																	? palette.grey[700]
																	: palette.grey[300]
														}`,
														boxShadow: dragSnapshot.isDragging
															? boxShadows.lg
															: darkMode
																? "none"
																: boxShadows.xs,
														transition: "box-shadow 0.2s ease, border-color 0.2s ease",
														"&:hover": {
															borderColor: palette.info.main
														}
													})}
												>
													{/* Drag handle */}
													<MDBox
														{...dragProvided.dragHandleProps}
														display="flex"
														alignItems="center"
														justifyContent="center"
														px={0.5}
														sx={({ palette }) => ({
															color: palette.grey[500],
															cursor: "grab",
															backgroundColor: darkMode
																? "rgba(255,255,255,0.03)"
																: palette.grey[100],
															"&:active": { cursor: "grabbing" },
															"&:hover": { color: palette.info.main }
														})}
													>
														<Icon>drag_indicator</Icon>
													</MDBox>

													{/* Label */}
													<MDBox display="flex" alignItems="center" px={1.5} py={1}>
														<MDTypography variant="button" fontWeight="medium">
															{item.label}
														</MDTypography>
													</MDBox>

													{/* Remove button */}
													<MDBox display="flex" alignItems="center" pr={0.5}>
														<Tooltip title={t("SETTINGS.delete")} placement="top" arrow>
															<IconButton
																size="small"
																onClick={e => {
																	e.stopPropagation();
																	removeItem(index);
																}}
															>
																<Icon fontSize="small" color="error">
																	close
																</Icon>
															</IconButton>
														</Tooltip>
													</MDBox>
												</MDBox>
											)}
										</Draggable>
									))}
									{provided.placeholder}

									{temporaryValues.length === 0 && (
										<MDBox
											display="flex"
											flexDirection="column"
											alignItems="center"
											justifyContent="center"
											flex="1"
											py={6}
											color="text.secondary"
										>
											<Icon fontSize="large" sx={{ opacity: 0.4, mb: 1 }}>
												view_column
											</Icon>
											<MDTypography variant="body2" color="text" textAlign="center">
												{t("DATATABLE.noColumnsSelectedDescription")}
											</MDTypography>
										</MDBox>
									)}
								</MDBox>
							)}
						</Droppable>
					</DragDropContext>
				</MDBox>
			</DialogContent>

			<Divider sx={{ m: 0 }} />

			<DialogActions sx={{ px: 3, py: 2 }}>
				<MDButton variant="outlined" color="secondary" onClick={handleClose}>
					{t("SETTINGS.cancel")}
				</MDButton>
				<MDButton
					variant="gradient"
					color="info"
					onClick={() => {
						const newOrder = temporaryValues.map(item => item.code);
						onSave(newOrder);
					}}
				>
					<Icon>check</Icon>&nbsp;{t("SETTINGS.save")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default ReorderColumnsDialog;
